import { FC } from 'react';
import clsx from 'clsx';
import { useTranslation } from 'next-i18next';
import Button from '@components/atoms/button/button';
import sanitizer from '@helpers/sanitizer';
import {
  useBrandSearchPresenter,
  BrandSearchProps,
  UNSIGNED_BRAND_ID,
  BRAND_MAX_LENGTH,
} from './brand-search-presenter';
import styles from './brand-search.module.scss';

const BrandNameWithHighlight = ({ brandName, searchTerm }) => {
  if (!brandName) {
    return '';
  }
  const searchTermStartIndex = brandName.toLowerCase().indexOf(searchTerm.toLowerCase());
  if (!searchTerm || searchTermStartIndex <= -1) {
    return <div className={styles.depositForm__form__optionsList__item__value}>{brandName}</div>;
  }
  return (
    <div className={styles.depositForm__form__optionsList__item__value}>
      {brandName.substring(0, searchTermStartIndex)}
      <span className={styles['depositForm__form__optionsList__item--strong']}>
        {brandName.substring(searchTermStartIndex, searchTermStartIndex + searchTerm.length)}
      </span>
      {brandName.substring(searchTermStartIndex + searchTerm.length)}
    </div>
  );
};

const BrandSearchError = ({ brandLengthError, brandHasError, isJewelry, isBanned }) => {
  const { t } = useTranslation('deposit-form');
  if (!brandHasError) {
    return null;
  }
  let errorMsg: string | React.ReactNode = '';
  if (brandLengthError) {
    errorMsg = t('DEPOSIT_FORM.BRAND_LENGTH_ERROR', { limitCharacter: BRAND_MAX_LENGTH });
  } else if (isBanned) {
    errorMsg = t('DEPOSIT_FORM.BRAND_ERROR');
  } else if (isJewelry) {
    errorMsg = t('DEPOSIT_FORM.BRAND_SERACH.NO_MATCH_ERROR_JEWELRY');
  } else {
    errorMsg = (
      <span
        dangerouslySetInnerHTML={{
          __html: sanitizer(t('DEPOSIT_FORM.BRAND_SEARCH.NO_MATCH_ERROR')),
        }}
      />
    );
  }
  return <p className="vc-inputText-errorMsg vc-text-s">{errorMsg}</p>;
};

export const BrandSearch: FC<BrandSearchProps> = ({
  universes,
  isBanned,
  setIsBanned,
  listingDraftPayload,
  setListingDraftAttribute,
  brandsByFilter,
  brandSearchTerm,
  setBrandSearchTerm,
  isJewelry,
}) => {
  const { t } = useTranslation('deposit-form');
  const {
    handleChangeBrand,
    handleSelectBrand,
    handleFocusBrand,
    handleKeyboardSelect,
    inputBrands,
    brandHasError,
    isJewelryUnsigned,
    keyboardSelectIndex,
    optionsElement,
    brandLengthError,
    getIsNoMatchDisplayedForNonJewelry,
  } = useBrandSearchPresenter({
    universes,
    isBanned,
    isJewelry,
    setIsBanned,
    listingDraftPayload,
    setListingDraftAttribute,
    brandsByFilter,
    brandSearchTerm,
    setBrandSearchTerm,
  });

  return (
    <div className={clsx('vc-formUnit', `${brandHasError ? 'error' : ''}`)}>
      <input
        id="depositForm__form__brands-input"
        data-cy="deposit-form-brands-input"
        name="depositForm__form__brands-input"
        autoComplete="off"
        className={clsx('vc-inputText', styles.depositForm__form__inputText, {
          [styles['depositForm__form__inputText--hasValue']]: brandSearchTerm,
        })}
        onChange={handleChangeBrand}
        onKeyDown={handleKeyboardSelect}
        onFocus={handleFocusBrand}
        value={brandSearchTerm}
        ref={inputBrands}
        placeholder={t('DEPOSIT_FORM.BRAND_NAME_PLACEHOLDER')}
      />
      <ul
        ref={optionsElement}
        className={clsx(styles.depositForm__form__optionsList, 'vc-d-none')}
      >
        {isJewelryUnsigned && (
          <li>
            <div className={styles.depositForm__form__optionsList__item__label}>
              {t('DEPOSIT_FORM.BRAND_SERACH.NO_MATCH_JEWELRY')}
            </div>
          </li>
        )}
        {brandsByFilter.length > 0 &&
          brandsByFilter.map((brand, index) => (
            <li key={brand.id}>
              <Button
                disableDefaultStyling
                data-cy="sell-clothes-online-select-brand-button"
                className={clsx(
                  styles.depositForm__form__optionsList__item,
                  { [styles['depositForm__form__optionsList__item--selected']]: keyboardSelectIndex === index },
                  { [styles['depositForm__form__optionsList__item--unsigned']]: brand.id === UNSIGNED_BRAND_ID },
                )}
                onClick={() => {
                  handleSelectBrand(brand);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleSelectBrand(brand);
                  }
                }}
                tabIndex={0}
              >
                {getIsNoMatchDisplayedForNonJewelry(brand) && (
                  <div
                    className={styles.depositForm__form__optionsList__item__label}
                    dangerouslySetInnerHTML={{
                      __html: sanitizer(t('DEPOSIT_FORM.BRAND_SEARCH.NO_MATCH_HINT')),
                    }}
                  />
                )}
                <BrandNameWithHighlight
                  brandName={brand.name}
                  searchTerm={brandSearchTerm}
                />
              </Button>
            </li>
          ))}
      </ul>
      {isJewelry && !listingDraftPayload.brand && !brandHasError && (
        <p className={styles.depositForm__form__inputText__jewelryHint + ' vc-inputText-label vc-text-s'}>
          {t('DEPOSIT_FORM.BRAND_SEARCH.JEWELRY_HINT')}
        </p>
      )}
      <BrandSearchError
        brandLengthError={brandLengthError}
        brandHasError={brandHasError}
        isJewelry={isJewelry}
        isBanned={isBanned}
      />
    </div>
  );
};
